import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { BsPersonFill, BsLockFill } from 'react-icons/bs';
import MUILinerProgress from '../../components/MUILinerProgress/MUILinerProgress';
import Input from '../../components/Input/Input';
import { login } from '../../redux/slices/authSlice';

const loginStyles = {
  wrapper: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    position: 'relative',
    overflow: 'hidden',
  },
  bgCircle1: {
    position: 'absolute',
    width: '300px',
    height: '300px',
    borderRadius: '50%',
    background: 'rgba(255, 255, 255, 0.08)',
    top: '-80px',
    left: '-80px',
  },
  bgCircle2: {
    position: 'absolute',
    width: '200px',
    height: '200px',
    borderRadius: '50%',
    background: 'rgba(255, 255, 255, 0.05)',
    bottom: '-50px',
    right: '-50px',
  },
  card: {
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(20px)',
    borderRadius: '16px',
    padding: '3rem 2.5rem',
    width: '100%',
    maxWidth: '420px',
    boxShadow: '0 25px 50px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.2)',
    position: 'relative',
    zIndex: 1,
    margin: '1rem',
  },
  logo: {
    width: '60px',
    height: '60px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    borderRadius: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 1.5rem',
    boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
  },
  logoText: {
    color: '#fff',
    fontSize: '1.8rem',
    fontWeight: '700',
    lineHeight: 1,
  },
  title: {
    textAlign: 'center',
    fontSize: '1.5rem',
    fontWeight: '600',
    color: '#2d3748',
    marginBottom: '0.25rem',
  },
  subtitle: {
    textAlign: 'center',
    fontSize: '0.875rem',
    color: '#a0aec0',
    marginBottom: '2rem',
  },
  inputGroup: {
    position: 'relative',
    marginBottom: '1rem',
  },
  inputIcon: {
    position: 'absolute',
    left: '14px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#a0aec0',
    fontSize: '1rem',
    zIndex: 2,
    pointerEvents: 'none',
  },
  input: {
    width: '100%',
    padding: '0.85rem 1rem 0.85rem 2.8rem',
    border: '2px solid #e2e8f0',
    borderRadius: '10px',
    fontSize: '0.9rem',
    color: '#2d3748',
    background: '#f7fafc',
    transition: 'all 0.2s ease',
    outline: 'none',
  },
  errorText: {
    color: '#e74a3b',
    fontSize: '0.75rem',
    marginTop: '4px',
    marginLeft: '4px',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '0.85rem',
    color: '#718096',
    margin: '0.5rem 0 1.5rem',
    cursor: 'pointer',
  },
  button: {
    width: '100%',
    padding: '0.85rem',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    fontSize: '0.95rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
    letterSpacing: '0.3px',
  },
  divider: {
    border: 'none',
    borderTop: '1px solid #e2e8f0',
    margin: '1.5rem 0',
  },
  link: {
    display: 'block',
    textAlign: 'center',
    color: '#667eea',
    textDecoration: 'none',
    fontSize: '0.85rem',
    fontWeight: '500',
    marginTop: '0.75rem',
    transition: 'color 0.2s ease',
  },
};

function LoginPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    auth: { isLoading, userInfo },
  } = useSelector(store => store);
  const [user, setUser] = useState({
    username: '',
    password: '',
  });
  const [error, setError] = useState({
    username: '',
    password: '',
  });

  const handleChangeUser = (name, value) => {
    setError({ ...error, [name]: '' });
    setUser({ ...user, [name]: value });
  };

  const handleChangeError = (name, value) => {
    setError({ ...error, [name]: value });
  };

  const handleSave = async userData => {
    if (!userData?.username && !userData?.password)
      setError({ username: 'Username required', password: 'Password required' });
    else if (!userData?.username) handleChangeError('username', 'Username required');
    else if (!userData?.password) handleChangeError('password', 'Password required');
    else if (userData?.username && userData.password) {
      await dispatch(login(userData));
    }
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      handleSave(user);
    }
  };

  useEffect(() => {
    userInfo?.access && navigate('/admin');
  }, [userInfo]);

  return (
    <div style={loginStyles.wrapper}>
      <div style={loginStyles.bgCircle1} />
      <div style={loginStyles.bgCircle2} />
      {isLoading && <MUILinerProgress />}

      <div style={loginStyles.card}>
        <div style={loginStyles.logo}>
          <span style={loginStyles.logoText}>M</span>
        </div>
        <h1 style={loginStyles.title}>Добро пожаловать!</h1>
        <p style={loginStyles.subtitle}>Войдите в свою учётную запись</p>

        <div style={loginStyles.inputGroup}>
          <span style={loginStyles.inputIcon}>
            <BsPersonFill />
          </span>
          <input
            id='login'
            type='text'
            placeholder='Имя пользователя'
            style={{
              ...loginStyles.input,
              borderColor: error.username ? '#e74a3b' : '#e2e8f0',
            }}
            onChange={e => handleChangeUser('username', e.target.value)}
            onKeyDown={handleKeyDown}
          />
          {error.username && <p style={loginStyles.errorText}>{error.username}</p>}
        </div>

        <div style={loginStyles.inputGroup}>
          <span style={loginStyles.inputIcon}>
            <BsLockFill />
          </span>
          <Input
            id='password'
            type='password'
            placeholder='Пароль'
            errorMessage={error.password}
            className='border-radius-0 outline-none color-gray'
            style={{
              ...loginStyles.input,
              paddingLeft: '2.8rem',
              borderColor: error.password ? '#e74a3b' : '#e2e8f0',
            }}
            onChange={e => handleChangeUser('password', e.target.value)}
            onKeyDown={handleKeyDown}
          />
        </div>

        <label style={loginStyles.checkboxLabel} htmlFor='check'>
          <input type='checkbox' id='check' />
          Запомни меня
        </label>

        <button
          style={{
            ...loginStyles.button,
            opacity: isLoading ? 0.7 : 1,
          }}
          type='submit'
          disabled={isLoading}
          onClick={() => handleSave(user)}
        >
          {isLoading ? 'Загрузка...' : 'Авторизоваться'}
        </button>

        <hr style={loginStyles.divider} />

        <Link to='/' style={loginStyles.link}>
          Забыл пароль?
        </Link>
      </div>
    </div>
  );
}

export default LoginPage;
